import { inject, ref, type Ref } from 'vue';
import { sectionItemIdKey } from './internal';

export type UseSectionIdOptions = {
  /**
   * When true, a missing `SectionNavItem` provider returns `ref('')` without a
   * dev warning. Use for callers that legitimately render both inside an item
   * (active-section rail) and outside (legacy sections → hover popover).
   */
  optional?: boolean;
};

/**
 * Returns the section id of the nearest `SectionNavItem` ancestor as a ref.
 * Falls back to an empty ref when called outside an item so isolated mounts and
 * tests don't crash.
 *
 * By default a missing provider logs a dev warning (likely a wiring mistake).
 * Pass `{ optional: true }` when the call site intentionally mounts outside a
 * `SectionNavItem` as well — e.g. sidebar action blocks that use empty id →
 * never active → hover popover for legacy `InputSection`/`TableSection` hosts.
 */
export function useSectionId(opts?: UseSectionIdOptions): Ref<string> {
  const injected = inject(sectionItemIdKey, null);
  if (!injected) {
    if (!opts?.optional && import.meta.env?.DEV) {
      console.warn(
        '[nx-ui] useSectionId() was called outside an <SectionNavItem>. Returning an empty section id.'
      );
    }
    return ref('');
  }
  return injected;
}
